/**
 * NinjaCash i18n core (F5 Language Plan Phase 0, approved 2026-07-10).
 *
 * Zero-dependency catalog engine adapted from the monorepo template
 * `features/typescript_language_selector/` (decision D3). Catalog format is
 * the monorepo standard shared with the Flask/PySide6 templates: nested JSON
 * flattened to dotted keys, `{placeholder}` interpolation, full-sentence
 * entries only, English fallback, missing key renders as the key itself.
 *
 * Laws (LANGUAGE_NJCASH.md):
 * - Language is per-device presentation state (localStorage, like themes).
 *   NEVER synced; switching must never touch finance data.
 * - Test environment is ALWAYS English (vitest gate below).
 * - `?lang=ja|th|en` is a supported override (persists) for Playwright/CDP
 *   smokes and debugging.
 */

// ============================================================
// Types
// ============================================================

export interface LanguageDefinition {
  code: string;
  englishName: string;
  nativeName: string; // selectors show THIS (native names only)
}

export type TranslationParams = Record<string, string | number>;

// ============================================================
// Catalog helpers (flatten / interpolate / parity audit)
// ============================================================

export function flattenCatalog(
  node: unknown,
  prefix = '',
  out: Record<string, string> = {}
): Record<string, string> {
  if (node === null || node === undefined) return out;
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    if (prefix) out[prefix] = String(node);
    return out;
  }
  if (typeof node === 'object' && !Array.isArray(node)) {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      flattenCatalog(value, prefix ? `${prefix}.${key}` : key, out);
    }
  }
  return out;
}

const PLACEHOLDER_RE = /\{([A-Za-z0-9_]+)\}/g;

export function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER_RE, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
  );
}

export function extractPlaceholders(template: string): string[] {
  const found = new Set<string>();
  for (const m of template.matchAll(PLACEHOLDER_RE)) found.add(m[1]);
  return [...found].sort();
}

export interface CatalogDiff {
  missingKeys: string[];
  extraKeys: string[];
  placeholderMismatches: string[];
  emptyValues: string[];
}

/** Parity audit - wired into catalogs.critical.test.ts (test:critical). */
export function compareCatalogs(base: unknown, other: unknown): CatalogDiff {
  const flatBase = flattenCatalog(base);
  const flatOther = flattenCatalog(other);
  const diff: CatalogDiff = {
    missingKeys: [],
    extraKeys: [],
    placeholderMismatches: [],
    emptyValues: [],
  };
  for (const key of Object.keys(flatBase)) {
    if (!(key in flatOther)) {
      diff.missingKeys.push(key);
      continue;
    }
    if (flatOther[key].trim() === '') diff.emptyValues.push(key);
    if (
      extractPlaceholders(flatBase[key]).join(',') !==
      extractPlaceholders(flatOther[key]).join(',')
    ) {
      diff.placeholderMismatches.push(key);
    }
  }
  for (const key of Object.keys(flatOther)) {
    if (!(key in flatBase)) diff.extraKeys.push(key);
  }
  diff.missingKeys.sort();
  diff.extraKeys.sort();
  diff.placeholderMismatches.sort();
  diff.emptyValues.sort();
  return diff;
}

// ============================================================
// Core factory
// ============================================================

export interface I18nConfig {
  languages: LanguageDefinition[];
  catalogs: Record<string, unknown>;
  defaultLanguage?: string;
  fallbackLanguage?: string;
  storageKey?: string; // '' disables persistence
  queryParam?: string | false;
  detectFromNavigator?: boolean;
  /** Forgiving input mappings, e.g. { jp: 'ja' } so ?lang=jp works. */
  aliases?: Record<string, string>;
}

export interface I18n {
  t(key: string, params?: TranslationParams): string;
  getLanguage(): string;
  setLanguage(code: string): void;
  readonly languages: readonly LanguageDefinition[];
  subscribe(listener: () => void): () => void;
  hasKey(key: string): boolean;
  getCatalog(code: string): Record<string, string>;
}

function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / disabled storage: language still works for the session.
  }
}

export function createI18n(config: I18nConfig): I18n {
  const languages = config.languages;
  const defaultLanguage = config.defaultLanguage ?? 'en';
  const fallbackLanguage = config.fallbackLanguage ?? 'en';
  const storageKey = config.storageKey ?? '';
  const queryParam = config.queryParam ?? false;
  const aliases = config.aliases ?? {};

  const flat: Record<string, Record<string, string>> = {};
  for (const [code, catalog] of Object.entries(config.catalogs)) {
    flat[code] = flattenCatalog(catalog);
  }

  const known = new Set(languages.map((l) => l.code));
  const listeners = new Set<() => void>();

  /** 'ja-JP' -> 'ja' when only the primary tag is configured. */
  function matchLanguage(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const norm = raw.replace(/_/g, '-');
    if (known.has(norm)) return norm;
    const lower = norm.toLowerCase();
    const aliased = aliases[lower];
    if (aliased && known.has(aliased)) return aliased;
    for (const c of known) {
      if (c.toLowerCase() === lower) return c;
    }
    const primary = lower.split('-')[0];
    for (const c of known) {
      if (c.split('-')[0].toLowerCase() === primary) return c;
    }
    return null;
  }

  function applyDocumentLang(code: string): void {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = code;
  }

  function resolveInitial(): string {
    // 1. ?lang= override (Playwright/CDP smokes, debugging) - persists.
    if (queryParam && typeof window !== 'undefined' && window.location) {
      try {
        const fromQuery = matchLanguage(new URLSearchParams(window.location.search).get(queryParam));
        if (fromQuery) {
          if (storageKey) safeStorageSet(storageKey, fromQuery);
          return fromQuery;
        }
      } catch {
        // ignore malformed query
      }
    }
    // 2. Persisted per-device choice.
    if (storageKey) {
      const stored = matchLanguage(safeStorageGet(storageKey));
      if (stored) return stored;
    }
    // 3. First-run browser detection (decision D2 - consumer app).
    if (config.detectFromNavigator && typeof navigator !== 'undefined') {
      const detected = matchLanguage(navigator.language);
      if (detected) return detected;
    }
    // 4. English.
    return matchLanguage(defaultLanguage) ?? languages[0].code;
  }

  let current = resolveInitial();
  applyDocumentLang(current);

  return {
    languages,

    t(key: string, params?: TranslationParams): string {
      const primary = flat[current]?.[key];
      if (primary !== undefined) return interpolate(primary, params);
      const fallback = flat[fallbackLanguage]?.[key];
      if (fallback !== undefined) return interpolate(fallback, params);
      return key; // visible = debuggable (obvious during testing)
    },

    getLanguage: () => current,

    setLanguage(code: string): void {
      const matched = matchLanguage(code);
      if (!matched) {
        console.warn(`[i18n] Unknown language "${code}" ignored`);
        return;
      }
      if (matched === current) return;
      current = matched;
      if (storageKey) safeStorageSet(storageKey, matched);
      applyDocumentLang(matched);
      for (const fn of [...listeners]) fn();
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    hasKey: (key: string) =>
      flat[current]?.[key] !== undefined || flat[fallbackLanguage]?.[key] !== undefined,

    getCatalog: (code: string) => ({ ...(flat[code] ?? {}) }),
  };
}
